import React, { FC, memo } from 'react';
import { Circle, Group, Line } from 'react-konva';
import { MIN_VERTEX_COUNT, VERTEX_HANDLE_HOVER_RADIUS_PX, VERTEX_HANDLE_RADIUS_PX } from '../model/constants/polygon';
import { Vertex } from '../utils/helpers/polygonVertices';

interface Props {
  vertices: Vertex[];
  rubberBand: Vertex | null;
  firstVertexHovered: boolean;
  axisLockGuide: { from: Vertex; to: Vertex } | null;
  canvasScale: number;
  stroke?: string;
}

const STROKE_COLOR = '#788593';

export const PolygonPreview: FC<Props> = memo(
  ({ vertices, rubberBand, firstVertexHovered, axisLockGuide, canvasScale, stroke = STROKE_COLOR }) => {
    if (!vertices.length) return null;

    const flat = vertices.flatMap((v) => [v.x, v.y]);
    const linePoints = rubberBand ? [...flat, rubberBand.x, rubberBand.y] : flat;

    return (
      <Group listening={false}>
        {/* Polyline from placed vertices to rubber-band cursor */}
        <Line points={linePoints} stroke={stroke} strokeWidth={2 / canvasScale} strokeScaleEnabled={false} />

        {/* Axis-lock guide: dashed line along the locked direction */}
        {axisLockGuide && (
          <Line
            points={[axisLockGuide.from.x, axisLockGuide.from.y, axisLockGuide.to.x, axisLockGuide.to.y]}
            stroke={stroke}
            strokeWidth={1 / canvasScale}
            dash={[2 / canvasScale, 4 / canvasScale]}
            opacity={0.5}
            strokeScaleEnabled={false}
          />
        )}

        {/* Vertex markers */}
        {vertices.map((v, i) => {
          const isFirst = i === 0;
          const isHovered = isFirst && firstVertexHovered && vertices.length >= MIN_VERTEX_COUNT;
          const radius = (isHovered ? VERTEX_HANDLE_HOVER_RADIUS_PX : VERTEX_HANDLE_RADIUS_PX) / canvasScale;

          return (
            <Circle
              key={i}
              x={v.x}
              y={v.y}
              radius={radius}
              fill={isHovered ? stroke : 'white'}
              stroke={stroke}
              strokeWidth={1 / canvasScale}
              strokeScaleEnabled={false}
            />
          );
        })}
      </Group>
    );
  },
);
